/**
 * LikesStep.foodFilters.js
 *
 * Pure functions, no React — shapes the Screen 8 "Likes" search catalog
 * using the person's Screen 7 "Food Preferences" answers (Diet Type,
 * Cuisine, Cooking Time, Budget).
 *
 * Per FSD v2.1 §6 Seven-Day Plan Definition: Diet Type is a **hard
 * filter**; Cooking Time and Budget are **soft ranking factors by
 * default** (a prior build treated them as hard filters — that's no
 * longer the closed baseline, see FoodPreferencesStep.constants.js's
 * FILTER_MODE / *_FILTER_MODE exports). Cuisine has always been a soft
 * preference. This module now mirrors those exact modes:
 *   - Diet Type → hard-excludes incompatible items from suggestions.
 *   - Cooking Time, Budget → soft-rank compatible-and-closer items first,
 *     never exclude.
 *   - Cuisine → never excludes; adds to the same ranking score.
 *
 * This does not affect what a person can type as a free-text custom
 * entry (see ALLOW_CUSTOM_ENTRIES in LikesStep.constants.js) — only the
 * catalog-backed suggestions and the exact-label Enter-to-add match.
 *
 * Screen 7 value formats, confirmed against FoodPreferencesStep.constants.js:
 *   - dietType: one of DIET_TYPE_OPTIONS ids — "vegetarian",
 *     "non_vegetarian", "eggetarian", "vegan", "jain".
 *   - cookingTime: one of COOKING_TIME_OPTIONS ids — "under_15", "15_30",
 *     "30_60", "60_plus" (or "" if unanswered).
 *   - budget: one of BUDGET_LEVELS ids — "budget", "moderate", "premium".
 *   - cuisines: array of CUISINE_OPTIONS ids.
 * Every catalog item in LikesStep.constants.js's FOOD_SEARCH_ITEMS
 * already carries matching compatibleDietTypes / cookingTimeBand /
 * budgetCategory / cuisineTags fields precomputed against those exact
 * ids (see that file's header) — this module compares against those
 * fields directly rather than re-deriving diet compatibility from raw
 * isMeat/isFish/… flags or re-parsing free-form cooking-time text (the
 * prior version did both, which is exactly the kind of duplicated logic
 * that drifts out of sync — e.g. its regex-based cooking-time parser
 * didn't recognize "60_plus" as unbounded, since it only matched a
 * literal "+" and "60_plus" spells the word "plus"). Every resolver
 * below still fails OPEN (skips that filter) on anything unrecognized,
 * as a safety net against a stale/legacy `foodPreferences` shape.
 */

const normalizeToken = (value) => String(value ?? "").toLowerCase().trim();

// ─── Diet Type (hard filter) ─────────────────────────────────────────────

const VALID_DIET_TYPES = ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"];

const resolveDietTypeToken = (rawDietType) => {
  const key = normalizeToken(rawDietType);
  return VALID_DIET_TYPES.includes(key) ? key : null;
};

// Reads the item's precomputed compatibleDietTypes (see
// LikesStep.constants.js) instead of re-deriving it from isMeat/isFish/…
// flags — single source of truth, can't drift out of sync with the
// constants file.
const isDietCompatible = (item, dietTypeToken) => {
  if (!Array.isArray(item.compatibleDietTypes)) return true; // no metadata to check — fail open
  return item.compatibleDietTypes.includes(dietTypeToken);
};

// ─── Cooking Time (soft ranking factor — FSD v2.1 §6) ────────────────────

const COOKING_TIME_BAND_ORDER = ["under_15", "15_30", "30_60", "60_plus"];

const resolveCookingTimeToken = (rawCookingTime) => {
  const key = normalizeToken(rawCookingTime);
  return COOKING_TIME_BAND_ORDER.includes(key) ? key : null;
};

// A person's selected band is a soft ceiling for ranking purposes — items
// within the selected band or faster score higher; nothing is excluded.
// Compares the item's precomputed cookingTimeBand by ordinal position
// (no free-text parsing, no risk of an unrecognized band silently
// falling through to "no upper bound").
const cookingTimeMatchScore = (item, cookingTimeToken) => {
  if (!cookingTimeToken || !item.cookingTimeBand) return 0;
  const itemRank = COOKING_TIME_BAND_ORDER.indexOf(item.cookingTimeBand);
  const selectedRank = COOKING_TIME_BAND_ORDER.indexOf(cookingTimeToken);
  if (itemRank === -1) return 0;
  return itemRank <= selectedRank ? 1 : 0;
};

// ─── Budget (soft ranking factor — FSD v2.1 §6) ───────────────────────────

const BUDGET_ORDER = ["budget", "moderate", "premium"];

const resolveBudgetToken = (rawBudget) => {
  const key = normalizeToken(rawBudget);
  return BUDGET_ORDER.includes(key) ? key : null;
};

// Same soft-ceiling convention as cooking time — "Moderate" should rank
// budget-tier foods highly too, not just moderate-tier ones, and never
// exclude a premium item outright.
const budgetMatchScore = (item, budgetToken) => {
  if (!budgetToken || !item.budgetCategory) return 0;
  const itemRank = BUDGET_ORDER.indexOf(item.budgetCategory);
  const selectedRank = BUDGET_ORDER.indexOf(budgetToken);
  if (itemRank === -1) return 0;
  return itemRank <= selectedRank ? 1 : 0;
};

// ─── Cuisine (soft preference — ranking only) ────────────────────────────

const VALID_CUISINES = [
  "north_indian", "south_indian", "gujarati", "punjabi",
  "bengali", "rajasthani", "continental", "chinese",
];

const normalizeCuisineList = (rawCuisines) => {
  if (!Array.isArray(rawCuisines)) return [];
  return rawCuisines.map(normalizeToken).filter((c) => VALID_CUISINES.includes(c));
};

const cuisineMatchScore = (item, cuisineTokens) => {
  if (cuisineTokens.length === 0 || !Array.isArray(item.cuisineTags)) return 0;
  return item.cuisineTags.filter((tag) => cuisineTokens.includes(tag)).length;
};

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * @param {Array}  items            - FOOD_SEARCH_ITEMS (or a pre-filtered
 *   subset).
 * @param {object} [foodPreferences] - Screen 7 answers:
 *   { dietType, cuisines, cookingTime, budget }. Any/all fields may be
 *   missing (e.g. before Screen 7 is reached, or if a field was skipped).
 * @returns {Array} Diet-Type-compatible items (hard filter), sorted with
 *   Cooking-Time/Budget/Cuisine matches ranked first (soft factors — none
 *   of the three ever excludes an item, per FSD v2.1 §6). Never throws on
 *   unrecognized or missing preference values — it just filters/ranks less.
 */
export function filterAndRankFoodItems(items, foodPreferences) {
  const dietToken = resolveDietTypeToken(foodPreferences?.dietType);
  const cookingTimeToken = resolveCookingTimeToken(foodPreferences?.cookingTime);
  const budgetToken = resolveBudgetToken(foodPreferences?.budget);
  const cuisineTokens = normalizeCuisineList(foodPreferences?.cuisines);

  const filtered = dietToken
    ? items.filter((item) => isDietCompatible(item, dietToken))
    : items.slice();

  const hasRankingSignal = cookingTimeToken || budgetToken || cuisineTokens.length > 0;
  if (!hasRankingSignal) return filtered;

  return filtered
    .map((item) => ({
      item,
      score:
        cookingTimeMatchScore(item, cookingTimeToken) +
        budgetMatchScore(item, budgetToken) +
        cuisineMatchScore(item, cuisineTokens),
    }))
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

/**
 * Short, human-readable summary of which Screen 7 answers are currently
 * shaping the Likes suggestions, for an optional UI hint. Returns null if
 * no recognizable preferences were supplied.
 */
export function describeActiveFilters(foodPreferences) {
  if (!foodPreferences) return null;
  const parts = [];

  if (foodPreferences.dietType) parts.push(`${foodPreferences.dietType} diet`);
  if (foodPreferences.cookingTime) parts.push(`${foodPreferences.cookingTime} cooking time`);
  if (foodPreferences.budget) parts.push(`${foodPreferences.budget} budget`);
  if (Array.isArray(foodPreferences.cuisines) && foodPreferences.cuisines.length > 0) {
    parts.push(`prioritizing ${foodPreferences.cuisines.join(", ")}`);
  }

  return parts.length > 0 ? `Personalized to your ${parts.join(" · ")}` : null;
}